<template>
  <div ref="anchorEl">
    <button
      @click="toggleSlideout"
      :aria-expanded="isOpen ? 'true' : 'false'"
      class="relative grid grid-cols-[70px_minmax(0,1fr)_16px] items-center py-2 text-sm font-medium transition-colors duration-200 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/50 overflow-hidden"
      :class="[isOpen ? 'text-white' : '']"
    >
      <img
        :src="icon"
        :alt="title + ' icon'"
        class="shrink-0 transition-all duration-200 justify-self-center"
        :class="[
          'w-6 h-6',
          isActive ? 'sidebar-icon-active' : '',
        ]"
      />
      <span
        class="whitespace-nowrap transition-all duration-200 pr-3"
        :class="props.isExpanded ? 'max-w-[120px] opacity-100' : 'max-w-0 opacity-0'"
      >
        {{ title }}
      </span>
      <svg
        v-if="props.isExpanded"
        :class="['w-4 h-4 justify-self-end transition-transform duration-200', isOpen ? 'rotate-180' : '']"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <teleport to="body">
      <transition name="fade-fast">
        <div v-if="isOpen" class="fixed inset-0 bg-black/50 z-[10000]" @click="closeSlideout"></div>
      </transition>

      <Motion
        v-if="isOpen"
        class="fixed w-80 bg-slate-900/95 backdrop-blur border-l border-slate-700/60 shadow-2xl z-[10001] will-change-transform"
        :style="{ left: 'calc(12.625rem + 2rem)', top: panelTop + 'px', height: panelHeight + 'px' }"
        :initial="{ x: -320, opacity: 0 }"
        :animate="{ x: 0, opacity: 1 }"
        :exit="{ x: -320, opacity: 0 }"
        :transition="{ type: 'spring', stiffness: 560, damping: 28, mass: 0.9 }"
        @click.stop
      >
        <div class="flex flex-col h-full">
          <div class="flex items-center justify-between p-6 border-b border-slate-700">
            <div>
              <h2 class="text-lg font-semibold text-white">{{ title }}</h2>
              <p class="text-sm text-slate-400">Quick access panels</p>
            </div>
            <button
              @click="closeSlideout"
              class="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="flex-1 overflow-visible p-6">
            <nav class="space-y-3" @click="closeSlideout">
              <router-link
                v-for="link in links"
                :key="link.to"
                :to="link.to"
                class="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors group"
                :class="[
                  'text-slate-300 hover:text-white hover:bg-slate-700/50',
                  isActiveLink(link.to) ? 'bg-slate-700/40 text-white' : '',
                ]"
              >
                <div class="p-2 rounded-lg bg-slate-700/40 group-hover:bg-primary-500/20 transition-colors">
                  <img :src="link.iconSrc" alt="" class="w-5 h-5" />
                </div>
                <div class="text-left">
                  <div class="font-medium">{{ link.title }}</div>
                  <div class="text-xs text-slate-500">{{ link.description }}</div>
                </div>
              </router-link>
            </nav>
          </div>

          <div class="p-6 border-t border-slate-700">
            <div class="text-xs text-slate-500 text-center">Admin Tools</div>
          </div>
        </div>
      </Motion>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, onBeforeUnmount, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { Motion } from 'motion-v'

const props = withDefaults(
  defineProps<{
    title: string
    icon: string
    isExpanded?: boolean
    links: Array<{
      to: string
      title: string
      description: string
      iconSrc: string
    }>
  }>(),
  {
    isExpanded: true,
  },
)

const emit = defineEmits<{
  (e: 'toggle', open: boolean): void
}>()

const isOpen = ref(false)
const anchorEl = ref<HTMLElement | null>(null)
const panelTop = ref(0)
const panelHeight = ref(window.innerHeight)
const route = useRoute()
const isActive = computed(() => {
  return props.links.some((link) => route.path === link.to || route.path.startsWith(`${link.to}/`))
})

function measurePanelBounds() {
  const el = anchorEl.value
  if (!el) return
  const panel = el.closest('.panel-neo') as HTMLElement | null
  if (panel) {
    const rect = panel.getBoundingClientRect()
    panelTop.value = rect.top
    panelHeight.value = rect.height
  } else {
    panelTop.value = 0
    panelHeight.value = window.innerHeight
  }
}

function toggleSlideout() {
  isOpen.value = !isOpen.value
  emit('toggle', isOpen.value)
  if (isOpen.value) nextTick(measurePanelBounds)
}

function closeSlideout() {
  if (!isOpen.value) return
  isOpen.value = false
  emit('toggle', false)
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeSlideout()
  }
}

function isActiveLink(path: string) {
  return route.path === path
}

onMounted(() => {
  nextTick(measurePanelBounds)
  window.addEventListener('resize', measurePanelBounds)
  window.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', measurePanelBounds)
  window.removeEventListener('keydown', handleEscape)
})

watch(
  () => route.fullPath,
  () => {
    closeSlideout()
  },
)
</script>

<style scoped>
.sidebar-icon-active {
  filter: brightness(0) saturate(100%) invert(92%) sepia(96%) saturate(6137%) hue-rotate(2deg) brightness(106%) contrast(106%);
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 120ms ease;
}
.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}
</style>

